import React, { useState, useMemo, useRef, useEffect } from 'react';
import { 
  Calendar, 
  ChevronLeft, 
  ChevronRight, 
  Plus, 
  Filter, 
  Search, 
  CheckCircle2, 
  Clock, 
  AlertTriangle, 
  AlertOctagon, 
  PlayCircle, 
  FileSpreadsheet, 
  FileText, 
  Kanban, 
  Edit3, 
  Trash2, 
  User, 
  Check, 
  Layers, 
  Maximize2,
  Minimize2,
  X,
  Target,
  Sparkles,
  ChevronDown,
  Bell,
  BellRing,
  Send,
  Share2,
  Copy,
  CheckCheck,
  MessageSquare,
  Mail,
  Flame,
  ShieldAlert,
  Info,
  ExternalLink,
  Download,
  FileDown,
  FolderDown
} from 'lucide-react';
import { ProjectItem, ProjectTask, ProjectStatus, TaskStatus, ProjectPriority } from '../types.ts';
import { exportProjectToExcel, exportAllProjectsSummaryToExcel } from '../utils/projectExcelExport.ts';
import { exportProjectToPDF, exportAllProjectsSummaryToPDF } from '../utils/projectPdfExport.ts';

export interface PMAlertNotification {
  id: string;
  timestamp: string;
  taskId: string;
  taskName: string;
  wbsCode: string;
  projectId: string;
  projectName: string;
  projectCode: string;
  businessUnit: string;
  pmName: string;
  assignee: string;
  status: 'RETRASADA' | 'BLOQUEADA';
  priority: string;
  endDate: string;
  reason?: string;
  mitigationPlan?: string;
  notifiedAt: string;
  channel: 'SISTEMA' | 'WHATSAPP' | 'CORREO';
  read: boolean;
}

interface WorkPlanGanttSubpageProps {
  projects: ProjectItem[];
  businessUnitName: string;
  selectedProjectId?: string;
  onUpdateProject: (project: ProjectItem) => void;
  onSaveProject?: (project: ProjectItem) => void;
  onDeleteProject?: (projectId: string) => void;
}

type GanttScale = 'days' | 'weeks' | 'months';

export const WorkPlanGanttSubpage: React.FC<WorkPlanGanttSubpageProps> = ({
  projects,
  businessUnitName,
  selectedProjectId,
  onUpdateProject,
  onSaveProject,
  onDeleteProject
}) => {
  const [activeProjectId, setActiveProjectId] = useState<string>(selectedProjectId || (projects[0]?.id || 'ALL'));
  const [scale, setScale] = useState<GanttScale>('weeks');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [collapsedPhases, setCollapsedPhases] = useState<Record<string, boolean>>({});

  // Date window state (offset in days/weeks/months from current date)
  const [timelineOffset, setTimelineOffset] = useState<number>(0);

  // Edit Task Modal
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Partial<ProjectTask> & { 
    targetProjectId?: string;
    alertReason?: string;
    alertMitigation?: string;
  }>({});

  // Edit Project Modal
  const [isEditProjectModalOpen, setIsEditProjectModalOpen] = useState(false);
  const [editingProjectData, setEditingProjectData] = useState<Partial<ProjectItem>>({});

  // Alert Center Modal
  const [isAlertCenterOpen, setIsAlertCenterOpen] = useState(false);
  const [alertTabFilter, setAlertTabFilter] = useState<'ALL' | 'BLOQUEADA' | 'RETRASADA'>('ALL');
  const [copiedAlertId, setCopiedAlertId] = useState<string | null>(null);

  // Gantt Export Modal
  const [isGanttExportModalOpen, setIsGanttExportModalOpen] = useState(false);
  const [exportTargetId, setExportTargetId] = useState<string>(activeProjectId || 'ALL');

  // Keep exportTargetId in sync when activeProjectId changes
  useEffect(() => {
    setExportTargetId(activeProjectId);
  }, [activeProjectId]);

  // Handler to export Gantt in PDF
  const handleExportGanttPDF = (targetId?: string) => {
    const pId = targetId || exportTargetId || activeProjectId;
    if (pId === 'ALL') {
      exportAllProjectsSummaryToPDF(projects, businessUnitName);
    } else {
      const targetProj = projects.find(p => p.id === pId) || currentProject || projects[0];
      if (targetProj) {
        exportProjectToPDF(targetProj);
      }
    }
  };

  // Handler to export Gantt in Excel
  const handleExportGanttExcel = async (targetId?: string) => {
    const pId = targetId || exportTargetId || activeProjectId;
    if (pId === 'ALL') {
      await exportAllProjectsSummaryToExcel(projects, businessUnitName);
    } else {
      const targetProj = projects.find(p => p.id === pId) || currentProject || projects[0];
      if (targetProj) {
        await exportProjectToExcel(targetProj);
      }
    }
  };

  // Handler to export both PDF and Excel
  const handleExportGanttBoth = async (targetId?: string) => {
    handleExportGanttPDF(targetId);
    await handleExportGanttExcel(targetId);
  };

  // Toast banner for automatic notification
  const [activeToast, setActiveToast] = useState<{
    id: string;
    title: string;
    message: string;
    type: 'RETRASADA' | 'BLOQUEADA' | 'SUCCESS';
    pmName: string;
    taskName: string;
  } | null>(null);

  // Tooltip
  const [hoveredTask, setHoveredTask] = useState<{ task: ProjectTask; x: number; y: number } | null>(null);

  // Active Project or All
  const currentProject = projects.find(p => p.id === activeProjectId);

  // Alert logs from localStorage
  const [alertLogs, setAlertLogs] = useState<PMAlertNotification[]>(() => {
    try {
      const saved = localStorage.getItem(`snf_pm_alerts_${businessUnitName}`);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Save alerts to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(`snf_pm_alerts_${businessUnitName}`, JSON.stringify(alertLogs));
    } catch (e) {
      console.error('Error saving alert logs', e);
    }
  }, [alertLogs, businessUnitName]);

  // Handle Project Delete from Gantt
  const handleDeleteCurrentProject = async () => {
    if (!currentProject || !onDeleteProject) return;
    const conf = window.showAppConfirm 
      ? await window.showAppConfirm(`¿Está seguro de eliminar el proyecto "${currentProject.name}" (${currentProject.code}) y todas sus actividades?`, 'Eliminar Proyecto')
      : window.confirm(`¿Está seguro de eliminar el proyecto "${currentProject.name}"?`);
    if (conf) {
      const deletedId = currentProject.id;
      const otherProject = projects.find(p => p.id !== deletedId);
      setActiveProjectId(otherProject ? otherProject.id : 'ALL');
      onDeleteProject(deletedId);
    }
  };

  // Handle Project Save from Gantt
  const handleSaveProjectForm = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProjectData.name || !editingProjectData.code) {
      window.showAppAlert?.('Por favor ingrese el código y nombre del proyecto.', 'Campos Requeridos');
      return;
    }
    const targetPrj = currentProject || projects.find(p => p.id === editingProjectData.id);
    const updated: ProjectItem = {
      id: editingProjectData.id || targetPrj?.id || `PRJ-${Date.now()}`,
      code: editingProjectData.code,
      name: editingProjectData.name,
      businessUnit: editingProjectData.businessUnit || businessUnitName,
      costCenterId: editingProjectData.costCenterId || targetPrj?.costCenterId,
      costCenterName: editingProjectData.costCenterName || targetPrj?.costCenterName || businessUnitName,
      clientName: editingProjectData.clientName || 'Planta Operativa',
      category: (editingProjectData.category as any) || 'MANTENIMIENTO',
      manager: editingProjectData.manager || 'Líder de Proyecto',
      sponsor: editingProjectData.sponsor || '',
      startDate: editingProjectData.startDate || new Date().toISOString().split('T')[0],
      targetEndDate: editingProjectData.targetEndDate || new Date().toISOString().split('T')[0],
      actualEndDate: editingProjectData.actualEndDate,
      status: (editingProjectData.status as ProjectStatus) || 'POR_INICIAR',
      priority: (editingProjectData.priority as ProjectPriority) || 'ALTA',
      budgetTotal: editingProjectData.budgetTotal,
      budgetCurrency: editingProjectData.budgetCurrency || 'MXN',
      description: editingProjectData.description || '',
      scopeObjectives: editingProjectData.scopeObjectives || '',
      progressPercent: editingProjectData.progressPercent ?? targetPrj?.progressPercent ?? 0,
      tasks: targetPrj?.tasks || [],
      createdAt: targetPrj?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    if (onSaveProject) {
      onSaveProject(updated);
    } else {
      onUpdateProject(updated);
    }
    setIsEditProjectModalOpen(false);
    setEditingProjectData({});
  };

  // Collect tasks with parent project metadata
  const allTasks = useMemo(() => {
    if (activeProjectId !== 'ALL' && currentProject) {
      return currentProject.tasks.map(t => ({ 
        ...t, 
        projectName: currentProject.name, 
        projectCode: currentProject.code,
        projectManager: currentProject.manager,
        clientName: currentProject.clientName
      }));
    }
    return projects.flatMap(p => p.tasks.map(t => ({ 
      ...t, 
      projectName: p.name, 
      projectCode: p.code,
      projectManager: p.manager,
      clientName: p.clientName
    })));
  }, [projects, activeProjectId, currentProject]);

  // Critical alerts calculation (Retrasadas o Bloqueadas)
  const criticalAlertTasks = useMemo(() => {
    return allTasks.filter(t => t.status === 'RETRASADA' || t.status === 'BLOQUEADA');
  }, [allTasks]);

  const blockedTasksCount = useMemo(() => {
    return criticalAlertTasks.filter(t => t.status === 'BLOQUEADA').length;
  }, [criticalAlertTasks]);

  const delayedTasksCount = useMemo(() => {
    return criticalAlertTasks.filter(t => t.status === 'RETRASADA').length;
  }, [criticalAlertTasks]);

  // Auto-clear active toast
  useEffect(() => {
    if (activeToast) {
      const timer = setTimeout(() => {
        setActiveToast(null);
      }, 6500);
      return () => clearTimeout(timer);
    }
  }, [activeToast]);

  // Helper to trigger automated PM alert
  const triggerAutomatedPMNotification = (
    task: ProjectTask,
    project: ProjectItem,
    reason?: string,
    mitigation?: string
  ) => {
    const alertId = `alt-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
    const nowStr = new Date().toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
    
    const newAlert: PMAlertNotification = {
      id: alertId,
      timestamp: new Date().toISOString(),
      taskId: task.id,
      taskName: task.name,
      wbsCode: task.wbsCode,
      projectId: project.id,
      projectName: project.name,
      projectCode: project.code,
      businessUnit: project.businessUnit || businessUnitName,
      pmName: project.manager || 'Líder de Proyecto',
      assignee: task.assignee || 'Sin Asignar',
      status: task.status as 'RETRASADA' | 'BLOQUEADA',
      priority: task.priority || 'ALTA',
      endDate: task.endDate,
      reason: reason || task.notes || (task.status === 'BLOQUEADA' ? 'Bloqueo operativo reportado en Gantt' : 'Desviación en cronograma de ejecución'),
      mitigationPlan: mitigation || 'Revisión prioritaria y plan de mitigación en coordinación con PM',
      notifiedAt: nowStr,
      channel: 'SISTEMA',
      read: false
    };

    setAlertLogs(prev => [newAlert, ...prev.filter(a => a.taskId !== task.id || a.status !== task.status)]);

    setActiveToast({
      id: alertId,
      title: task.status === 'BLOQUEADA' ? '🚨 TAREA BLOQUEADA - ALERTA AL PM' : '⚠️ TAREA RETRASADA - ALERTA AL PM',
      message: `Notificación automática enviada a ${project.manager} para la actividad "${task.name}".`,
      type: task.status as 'RETRASADA' | 'BLOQUEADA',
      pmName: project.manager,
      taskName: task.name
    });
  };

  // WhatsApp Alert dispatch formatter
  const handleSendWhatsAppAlert = (alert: {
    businessUnit: string;
    projectCode: string;
    projectName: string;
    pmName: string;
    status: string;
    wbsCode: string;
    taskName: string;
    assignee: string;
    endDate: string;
    reason?: string;
    mitigationPlan?: string;
    notifiedAt?: string;
  }) => {
    const icon = alert.status === 'BLOQUEADA' ? '🛑' : '⚠️';
    const text = 
`🚨 *ALERTA AUTOMÁTICA DE PROYECTO - ISN OPERACIONES* 🚨
${icon} *ESTADO:* ${alert.status}
🏢 *Sede / BU:* ${alert.businessUnit}
📁 *Proyecto:* ${alert.projectCode} - ${alert.projectName}
👤 *Responsable PM:* ${alert.pmName}
📌 *Actividad (WBS ${alert.wbsCode}):* ${alert.taskName}
👷 *Asignado a:* ${alert.assignee}
📅 *Fecha Fin Compromiso:* ${alert.endDate}
🔍 *Causa / Bloqueador:* ${alert.reason || 'Desviación en cronograma detectada'}
🛠️ *Plan de Mitigación:* ${alert.mitigationPlan || 'Por definir con el Líder PM'}
⏰ *Emitido:* ${alert.notifiedAt || new Date().toLocaleString('es-MX')}

_Notificación generada automáticamente desde el Plan de Trabajo (Gantt) de ISN Portal._`;

    const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  // Email Alert dispatch formatter
  const handleSendEmailAlert = (alert: {
    businessUnit: string;
    projectCode: string;
    projectName: string;
    pmName: string;
    status: string;
    wbsCode: string;
    taskName: string;
    assignee: string;
    endDate: string;
    reason?: string;
    mitigationPlan?: string;
  }) => {
    const subject = `[ALERTA ${alert.status} PM] ${alert.projectCode} - Actividad WBS ${alert.wbsCode}: ${alert.taskName}`;
    const body = 
`Estimado(a) ${alert.pmName},

Se emite la presente ALERTA AUTOMÁTICA respecto a una desviación en el Plan de Trabajo:

• Sede / Centro de Costos: ${alert.businessUnit}
• Proyecto: ${alert.projectCode} - ${alert.projectName}
• Actividad Afectada: [WBS ${alert.wbsCode}] ${alert.taskName}
• Estado Actual: ${alert.status}
• Responsable Operativo: ${alert.assignee}
• Fecha Compromiso: ${alert.endDate}
• Motivo / Bloqueador: ${alert.reason || 'Sin especificar'}
• Plan de Mitigación Propuesto: ${alert.mitigationPlan || 'Por coordinar con líder técnico'}

Favor de revisar el cronograma en el portal ISN y tomar las acciones de escalación pertinentes.

Atentamente,
Sistema de Control y Gestión de Proyectos ISN`;

    const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
  };

  // Copy alert report text
  const handleCopyAlertReport = (alert: any, id: string) => {
    const text = 
`[ALERTA ISN - ${alert.status}]
Proyecto: ${alert.projectCode} - ${alert.projectName} | PM: ${alert.pmName}
Actividad: WBS ${alert.wbsCode} - ${alert.taskName} (${alert.assignee})
Fecha Límite: ${alert.endDate}
Motivo: ${alert.reason || 'Desviación en Gantt'}
Mitigación: ${alert.mitigationPlan || 'Revisión técnica'}`;

    navigator.clipboard?.writeText(text);
    setCopiedAlertId(id);
    setTimeout(() => setCopiedAlertId(null), 2500);
  };

  // Filter tasks
  const filteredTasks = useMemo(() => {
    return allTasks.filter(t => {
      const matchesSearch = 
        t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.assignee.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.phase.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (t.wbsCode && t.wbsCode.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesStatus = statusFilter === 'ALL' || t.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [allTasks, searchQuery, statusFilter]);

  // Group tasks by Phase
  const tasksByPhase = useMemo(() => {
    const map = new Map<string, typeof filteredTasks>();
    filteredTasks.forEach(task => {
      const phase = task.phase || 'General';
      if (!map.has(phase)) {
        map.set(phase, []);
      }
      map.get(phase)!.push(task);
    });
    return map;
  }, [filteredTasks]);

  // Calculate Timeline Bounds
  const { timelineStart, timelineEnd, totalUnits, unitWidth, unitsList } = useMemo(() => {
    // Determine base window start & end
    const now = new Date();
    // Default anchor around current project start or today
    let baseDate = new Date();
    if (currentProject) {
      const pStart = new Date(currentProject.startDate);
      if (!isNaN(pStart.getTime())) {
        baseDate = pStart;
      }
    }

    if (scale === 'days') {
      const start = new Date(baseDate);
      start.setDate(start.getDate() - 5 + timelineOffset * 15);
      const totalDays = 45;
      const end = new Date(start);
      end.setDate(end.getDate() + totalDays);

      const units: { label: string; subLabel: string; date: Date; isToday: boolean; isWeekend: boolean }[] = [];
      const curr = new Date(start);
      const todayStr = now.toISOString().split('T')[0];

      while (curr < end) {
        const currStr = curr.toISOString().split('T')[0];
        const dayOfWeek = curr.getDay();
        units.push({
          label: curr.toLocaleDateString('es-MX', { weekday: 'narrow' }),
          subLabel: `${curr.getDate()} ${curr.toLocaleDateString('es-MX', { month: 'short' })}`,
          date: new Date(curr),
          isToday: currStr === todayStr,
          isWeekend: dayOfWeek === 0 || dayOfWeek === 6
        });
        curr.setDate(curr.getDate() + 1);
      }

      return { timelineStart: start, timelineEnd: end, totalUnits: units.length, unitWidth: 44, unitsList: units };
    } 
    
    if (scale === 'weeks') {
      const start = new Date(baseDate);
      start.setDate(start.getDate() - start.getDay() + 1 + timelineOffset * 14); // Start Monday
      const totalWeeks = 16;
      const end = new Date(start);
      end.setDate(end.getDate() + totalWeeks * 7);

      const units: { label: string; subLabel: string; date: Date; isToday: boolean; isWeekend: boolean }[] = [];
      const curr = new Date(start);
      const todayTime = now.getTime();

      while (curr < end) {
        const weekEnd = new Date(curr);
        weekEnd.setDate(weekEnd.getDate() + 6);
        const isCurrentWeek = todayTime >= curr.getTime() && todayTime <= weekEnd.getTime();

        units.push({
          label: `Sem ${units.length + 1}`,
          subLabel: `${curr.getDate()} ${curr.toLocaleDateString('es-MX', { month: 'short' })}`,
          date: new Date(curr),
          isToday: isCurrentWeek,
          isWeekend: false
        });
        curr.setDate(curr.getDate() + 7);
      }

      return { timelineStart: start, timelineEnd: end, totalUnits: units.length, unitWidth: 72, unitsList: units };
    }

    // Months scale
    const start = new Date(baseDate.getFullYear(), baseDate.getMonth() - 1 + timelineOffset, 1);
    const totalMonths = 10;
    const end = new Date(start.getFullYear(), start.getMonth() + totalMonths, 0);

    const units: { label: string; subLabel: string; date: Date; isToday: boolean; isWeekend: boolean }[] = [];
    const curr = new Date(start);

    while (curr <= end) {
      const isCurrentMonth = now.getFullYear() === curr.getFullYear() && now.getMonth() === curr.getMonth();
      units.push({
        label: curr.toLocaleDateString('es-MX', { month: 'long' }).toUpperCase(),
        subLabel: `${curr.getFullYear()}`,
        date: new Date(curr),
        isToday: isCurrentMonth,
        isWeekend: false
      });
      curr.setMonth(curr.getMonth() + 1);
    }

    return { timelineStart: start, timelineEnd: end, totalUnits: units.length, unitWidth: 120, unitsList: units };
  }, [scale, timelineOffset, currentProject]);

  // Helper to compute task bar position & width
  const computeBarGeometry = (startDateStr: string, endDateStr: string) => {
    const sDate = new Date(startDateStr);
    const eDate = new Date(endDateStr);
    if (isNaN(sDate.getTime()) || isNaN(eDate.getTime())) {
      return { left: 0, width: 0, visible: false };
    }

    const tStartMs = timelineStart.getTime();
    const tEndMs = timelineEnd.getTime();
    const totalMs = tEndMs - tStartMs;
    const totalPx = totalUnits * unitWidth;

    const sOffsetMs = sDate.getTime() - tStartMs;
    const eOffsetMs = eDate.getTime() - tStartMs;

    const left = (sOffsetMs / totalMs) * totalPx;
    const width = Math.max(16, ((eOffsetMs - sOffsetMs) / totalMs) * totalPx);

    const visible = left + width >= 0 && left <= totalPx;

    return { left, width, visible };
  };

  // Helper for today vertical line
  const todayGeometry = useMemo(() => {
    const nowMs = Date.now();
    const tStartMs = timelineStart.getTime();
    const tEndMs = timelineEnd.getTime();
    const totalMs = tEndMs - tStartMs;
    const totalPx = totalUnits * unitWidth;

    if (nowMs < tStartMs || nowMs > tEndMs) return null;
    const left = ((nowMs - tStartMs) / totalMs) * totalPx;
    return left;
  }, [timelineStart, timelineEnd, totalUnits, unitWidth]);

  const togglePhaseCollapse = (phase: string) => {
    setCollapsedPhases(prev => ({ ...prev, [phase]: !prev[phase] }));
  };

  const handleOpenAddTask = (phaseName?: string) => {
    const parentPrj = currentProject || projects[0];
    setEditingTask({
      id: `tsk-${Date.now()}`,
      projectId: parentPrj?.id || '',
      targetProjectId: parentPrj?.id || '',
      wbsCode: `${allTasks.length + 1}.1`,
      name: '',
      phase: phaseName || 'Fase 1: Ejecución y Desarrollo',
      assignee: '',
      assigneeRole: 'Técnico Responsable',
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(Date.now() + 10 * 86400000).toISOString().split('T')[0],
      durationDays: 10,
      progressPercent: 0,
      status: 'POR_INICIAR',
      priority: 'ALTA',
      deliverable: '',
      isMilestone: false
    });
    setIsTaskModalOpen(true);
  };

  const handleSaveTaskForm = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTask.name) {
      window.showAppAlert('Por favor ingrese el nombre de la tarea.', 'Campo Requerido');
      return;
    }

    const targetPrjId = editingTask.targetProjectId || editingTask.projectId || currentProject?.id || projects[0]?.id;
    const prj = projects.find(p => p.id === targetPrjId);
    if (!prj) return;

    const sDate = new Date(editingTask.startDate || new Date().toISOString().split('T')[0]);
    const eDate = new Date(editingTask.endDate || new Date().toISOString().split('T')[0]);
    const diffDays = Math.max(1, Math.round((eDate.getTime() - sDate.getTime()) / 86400000));

    const finalTask: ProjectTask = {
      id: editingTask.id || `tsk-${Date.now()}`,
      projectId: targetPrjId,
      wbsCode: editingTask.wbsCode || `${prj.tasks.length + 1}.1`,
      name: editingTask.name,
      phase: editingTask.phase || 'Fase 1: Ejecución',
      assignee: editingTask.assignee || 'Sin Asignar',
      assigneeRole: editingTask.assigneeRole || 'Especialista',
      startDate: editingTask.startDate || new Date().toISOString().split('T')[0],
      endDate: editingTask.endDate || new Date().toISOString().split('T')[0],
      durationDays: diffDays,
      progressPercent: editingTask.progressPercent ?? 0,
      status: (editingTask.status as TaskStatus) || 'POR_INICIAR',
      priority: (editingTask.priority as ProjectPriority) || 'ALTA',
      deliverable: editingTask.deliverable || '',
      isMilestone: !!editingTask.isMilestone,
      notes: editingTask.notes || ''
    };

    let updatedTasks: ProjectTask[];
    const exists = prj.tasks.some(t => t.id === finalTask.id);
    if (exists) {
      updatedTasks = prj.tasks.map(t => t.id === finalTask.id ? finalTask : t);
    } else {
      updatedTasks = [...prj.tasks, finalTask];
    }

    // Recalculate project progress
    const totalProg = updatedTasks.reduce((acc, t) => acc + t.progressPercent, 0);
    const avgProg = updatedTasks.length > 0 ? Math.round(totalProg / updatedTasks.length) : 0;

    const updatedProject: ProjectItem = {
      ...prj,
      tasks: updatedTasks,
      progressPercent: avgProg,
      updatedAt: new Date().toISOString()
    };

    onUpdateProject(updatedProject);

    // AUTOMATED PM ALERT DISPATCH FOR 'RETRASADA' OR 'BLOQUEADA'
    if (finalTask.status === 'RETRASADA' || finalTask.status === 'BLOQUEADA') {
      triggerAutomatedPMNotification(
        finalTask, 
        prj, 
        editingTask.alertReason, 
        editingTask.alertMitigation
      );
    }

    setIsTaskModalOpen(false);
    setEditingTask({});
  };

  const handleToggleTaskComplete = (task: ProjectTask) => {
    const prj = projects.find(p => p.id === task.projectId);
    if (!prj) return;

    const newProgress = task.progressPercent === 100 ? 0 : 100;
    const newStatus: TaskStatus = newProgress === 100 ? 'COMPLETADA' : 'EN_PROCESO';

    const updatedTasks = prj.tasks.map(t => t.id === task.id ? { ...t, progressPercent: newProgress, status: newStatus } : t);
    const avgProg = Math.round(updatedTasks.reduce((acc, t) => acc + t.progressPercent, 0) / updatedTasks.length);

    onUpdateProject({
      ...prj,
      tasks: updatedTasks,
      progressPercent: avgProg
    });
  };

  const handleDeleteTask = async (task: ProjectTask) => {
    const conf = await window.showAppConfirm(`¿Eliminar la actividad "${task.name}"?`, 'Eliminar Tarea');
    if (!conf) return;

    const prj = projects.find(p => p.id === task.projectId);
    if (!prj) return;

    const updatedTasks = prj.tasks.filter(t => t.id !== task.id);
    const avgProg = updatedTasks.length > 0 
      ? Math.round(updatedTasks.reduce((acc, t) => acc + t.progressPercent, 0) / updatedTasks.length) 
      : 0;

    onUpdateProject({
      ...prj,
      tasks: updatedTasks,
      progressPercent: avgProg
    });
  };

  const getTaskStatusPill = (status: TaskStatus) => {
    switch (status) {
      case 'COMPLETADA':
        return <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-emerald-950/80 text-emerald-300 border border-emerald-500/30">Completada</span>;
      case 'EN_PROCESO':
        return <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-sky-950/80 text-sky-300 border border-sky-500/30">En Proceso</span>;
      case 'RETRASADA':
        return <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-rose-950/80 text-rose-300 border border-rose-500/30">Retrasada</span>;
      case 'BLOQUEADA':
        return <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-red-950/80 text-red-300 border border-red-500/30">Bloqueada</span>;
      default:
        return <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-slate-800 text-slate-300 border border-slate-700">Por Iniciar</span>;
    }
  };

  return (
    <div className="space-y-4">
      
      {/* TOP GANTT TOOLBAR & PROJECT SELECTOR */}
      <div className="bg-[#16171d] border border-[#272935] rounded-2xl p-4 space-y-3 shadow-md">
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
          
          {/* Project Selector Dropdown */}
          <div className="flex items-center gap-2 flex-wrap flex-1">
            <div className="flex items-center gap-1.5 bg-[#101115] border border-[#2a2c3c] rounded-xl px-3 py-2 text-xs text-white">
              <Layers className="w-4 h-4 text-red-500 shrink-0" />
              <span className="text-slate-400 font-medium">Proyecto:</span>
              <select
                value={activeProjectId}
                onChange={(e) => setActiveProjectId(e.target.value)}
                className="bg-transparent font-bold text-white focus:outline-none cursor-pointer pr-2"
              >
                <option value="ALL" className="bg-[#16171d] text-white">★ Todos los Proyectos de la Unidad ({projects.length})</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id} className="bg-[#16171d] text-white">
                    {p.code} - {p.name} ({p.progressPercent}%)
                  </option>
                ))}
              </select>
            </div>

            {/* Quick search input */}
            <div className="relative min-w-[180px]">
              <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-400" />
              <input
                type="text"
                placeholder="Filtrar actividades..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl pl-8 pr-3 py-1.5 text-xs text-white placeholder:text-slate-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Scale & Navigation Controls */}
          <div className="flex items-center gap-2 flex-wrap justify-between lg:justify-end">
            
            {/* Scale Selector: Días / Semanas / Meses */}
            <div className="flex items-center bg-[#101115] border border-[#2a2c3c] rounded-xl p-0.5 text-xs font-bold">
              <button
                onClick={() => setScale('days')}
                className={`px-2.5 py-1 rounded-lg transition-all cursor-pointer ${
                  scale === 'days' ? 'bg-red-600 text-white shadow' : 'text-slate-400 hover:text-white'
                }`}
              >
                Días
              </button>
              <button
                onClick={() => setScale('weeks')}
                className={`px-2.5 py-1 rounded-lg transition-all cursor-pointer ${
                  scale === 'weeks' ? 'bg-red-600 text-white shadow' : 'text-slate-400 hover:text-white'
                }`}
              >
                Semanas
              </button>
              <button
                onClick={() => setScale('months')}
                className={`px-2.5 py-1 rounded-lg transition-all cursor-pointer ${
                  scale === 'months' ? 'bg-red-600 text-white shadow' : 'text-slate-400 hover:text-white'
                }`}
              >
                Meses
              </button>
            </div>

            {/* Timeline Shift Buttons */}
            <div className="flex items-center gap-1 bg-[#101115] border border-[#2a2c3c] rounded-xl p-0.5">
              <button
                onClick={() => setTimelineOffset(prev => prev - 1)}
                className="p-1 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg cursor-pointer"
                title="Desplazar a la izquierda"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setTimelineOffset(0)}
                className="px-2 py-1 text-[11px] font-bold text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg cursor-pointer"
              >
                Hoy
              </button>
              <button
                onClick={() => setTimelineOffset(prev => prev + 1)}
                className="p-1 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg cursor-pointer"
                title="Desplazar a la derecha"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Alert Center Button */}
            <button
              onClick={() => setIsAlertCenterOpen(true)}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer ${
                criticalAlertTasks.length > 0
                  ? 'bg-rose-950/90 hover:bg-rose-900 border border-rose-500 text-rose-200 shadow-lg shadow-rose-950/50 animate-pulse'
                  : 'bg-[#101115] hover:bg-[#1a1b24] border border-[#2a2c3c] text-slate-300'
              }`}
              title="Centro de Alertas y Notificaciones al PM"
            >
              <BellRing className={`w-3.5 h-3.5 ${criticalAlertTasks.length > 0 ? 'text-rose-400' : 'text-slate-400'}`} />
              <span>Alertas PM</span>
              {criticalAlertTasks.length > 0 && (
                <span className="bg-rose-600 text-white font-mono text-[10px] px-1.5 py-0.2 rounded-full font-black ml-0.5 shadow">
                  {criticalAlertTasks.length}
                </span>
              )}
            </button>

            {/* Descargar Gantt Master Button */}
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => {
                  setExportTargetId(activeProjectId);
                  setIsGanttExportModalOpen(true);
                }}
                className="px-3.5 py-1.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-md shadow-emerald-950/40 transition-all cursor-pointer group"
                title="Descargar Plan de Trabajo y Cronograma Gantt en PDF o Excel"
              >
                <Download className="w-3.5 h-3.5 text-emerald-200 group-hover:translate-y-0.5 transition-transform" />
                <span>Descargar Gantt</span>
                <ChevronDown className="w-3 h-3 text-emerald-200 ml-0.5 opacity-80" />
              </button>

              {/* Quick 1-click direct download buttons */}
              <button
                onClick={() => handleExportGanttPDF()}
                className="p-1.5 rounded-xl bg-rose-950/50 hover:bg-rose-900/70 text-rose-300 border border-rose-500/40 transition-all cursor-pointer"
                title={currentProject ? `Descargar PDF de ${currentProject.code}` : 'Descargar Portafolio PDF'}
              >
                <FileText className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleExportGanttExcel()}
                className="p-1.5 rounded-xl bg-emerald-950/50 hover:bg-emerald-900/70 text-emerald-300 border border-emerald-500/40 transition-all cursor-pointer"
                title={currentProject ? `Descargar Excel de ${currentProject.code}` : 'Descargar Portafolio Excel'}
              >
                <FileSpreadsheet className="w-4 h-4" />
              </button>
            </div>

            {/* Actions: Add Task */}
            <button
              onClick={() => handleOpenAddTask()}
              className="px-3.5 py-1.5 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              <span>Nueva Actividad</span>
            </button>

            {currentProject && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => {
                    setEditingProjectData(currentProject);
                    setIsEditProjectModalOpen(true);
                  }}
                  className="px-2.5 py-1.5 rounded-xl bg-amber-950/40 hover:bg-amber-900/60 text-amber-300 border border-amber-500/30 text-xs font-bold flex items-center gap-1 transition-all cursor-pointer"
                  title="Editar Información del Proyecto"
                >
                  <Edit3 className="w-3.5 h-3.5" />
                  <span>Editar</span>
                </button>
                <button
                  onClick={handleDeleteCurrentProject}
                  className="p-1.5 rounded-xl bg-red-950/40 hover:bg-red-900/60 text-red-400 border border-red-500/30 transition-all cursor-pointer"
                  title="Eliminar Proyecto"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}

          </div>

        </div>

        {/* Selected Project Summary Sub-bar */}
        {currentProject && (
          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-[#232634] text-xs text-slate-300">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-slate-400 font-mono font-bold text-sky-400 bg-sky-950/50 px-2 py-0.5 rounded border border-sky-500/30">
                {currentProject.code}
              </span>
              <span className="font-bold text-white">{currentProject.name}</span>
              <span className="text-slate-400">• PM: <strong className="text-white">{currentProject.manager}</strong></span>
              <span className="text-slate-400">• Cliente: <strong className="text-white">{currentProject.clientName}</strong></span>
              <span className="text-slate-400">• Ventana: <strong className="text-white">{currentProject.startDate} al {currentProject.targetEndDate}</strong></span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-slate-400">Avance del Plan:</span>
                <div className="w-28 bg-[#101115] border border-[#272935] h-2 rounded-full overflow-hidden">
                  <div className="bg-emerald-500 h-full rounded-full" style={{ width: `${currentProject.progressPercent}%` }} />
                </div>
                <span className="font-mono font-bold text-emerald-400">{currentProject.progressPercent}%</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CRITICAL PM ALERT BANNER (If any task is RETRASADA or BLOQUEADA) */}
      {criticalAlertTasks.length > 0 && (
        <div className="bg-gradient-to-r from-rose-950/90 via-[#221016] to-[#15161e] border-2 border-rose-500/60 rounded-2xl p-4 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-3 animate-in fade-in slide-in-from-top-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-600/30 border border-rose-500/60 flex items-center justify-center shrink-0 animate-pulse text-rose-300">
              <ShieldAlert className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-black text-rose-400 uppercase tracking-wider font-mono">
                  🚨 Sistema de Alerta PM Activo:
                </span>
                <span className="text-xs font-bold text-white">
                  {criticalAlertTasks.length} {criticalAlertTasks.length === 1 ? 'actividad con desviación' : 'actividades con desviación'}
                </span>
                {blockedTasksCount > 0 && (
                  <span className="bg-red-900/90 text-red-100 border border-red-500/60 text-[10px] font-black px-2 py-0.5 rounded-full flex items-center gap-1">
                    <AlertOctagon className="w-3 h-3" /> {blockedTasksCount} Bloqueada{blockedTasksCount > 1 ? 's' : ''}
                  </span>
                )}
                {delayedTasksCount > 0 && (
                  <span className="bg-amber-900/90 text-amber-100 border border-amber-500/60 text-[10px] font-black px-2 py-0.5 rounded-full flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> {delayedTasksCount} Retrasada{delayedTasksCount > 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-300 mt-0.5">
                Se detectaron actividades críticas con desviación de cronograma o bloqueos. El sistema notifica automáticamente a los líderes PM responsables.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-end md:self-auto shrink-0 flex-wrap">
            <button
              onClick={() => {
                setStatusFilter(statusFilter === 'RETRASADA' || statusFilter === 'BLOQUEADA' ? 'ALL' : 'RETRASADA');
              }}
              className="px-3 py-1.5 rounded-xl bg-[#101115] hover:bg-slate-800 border border-slate-700 text-slate-200 text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              <Filter className="w-3.5 h-3.5 text-amber-400" />
              <span>{statusFilter === 'ALL' ? 'Filtrar Solo Alertas en Gantt' : 'Ver Todo el Cronograma'}</span>
            </button>
            <button
              onClick={() => setIsAlertCenterOpen(true)}
              className="px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-500 hover:to-red-500 text-white text-xs font-bold flex items-center gap-1.5 shadow-lg shadow-rose-600/30 transition-all cursor-pointer"
            >
              <BellRing className="w-3.5 h-3.5" />
              <span>Centro de Alertas PM</span>
            </button>
          </div>
        </div>
      )}

      {/* MAIN INTERACTIVE GANTT WORKSPACE */}
      <div className="bg-[#16171d] border border-[#272935] rounded-2xl overflow-hidden shadow-xl flex flex-col">
        
        <div className="flex divide-x divide-[#272935] overflow-x-auto min-h-[520px]">
          
          {/* LEFT PANE: WBS & ACTIVITY TABLE (Fixed width / collapsible) */}
          <div className="w-[380px] lg:w-[440px] shrink-0 bg-[#14151b] flex flex-col select-none">
            
            {/* Left Header */}
            <div className="h-14 border-b border-[#272935] bg-[#101115] px-4 flex items-center justify-between text-[11px] font-bold text-slate-400 uppercase tracking-wider font-mono">
              <div className="flex items-center gap-2">
                <span>WBS</span>
                <span>Actividad / Plan de Trabajo</span>
              </div>
              <div className="flex items-center gap-4">
                <span>Resp.</span>
                <span>%</span>
              </div>
            </div>

            {/* Left Body: Phases and Task Items */}
            <div className="divide-y divide-[#21232e] overflow-y-auto max-h-[700px]">
              {Array.from(tasksByPhase.entries()).map(([phaseName, phaseTasks]) => {
                const isCollapsed = collapsedPhases[phaseName];
                const phaseProgress = Math.round(phaseTasks.reduce((a, t) => a + t.progressPercent, 0) / phaseTasks.length);

                return (
                  <div key={phaseName} className="group">
                    {/* Phase Header Row */}
                    <div 
                      onClick={() => togglePhaseCollapse(phaseName)}
                      className="h-10 bg-[#1c1d26] hover:bg-[#232532] px-3 flex items-center justify-between text-xs font-bold text-white cursor-pointer transition-colors border-l-2 border-red-500"
                    >
                      <div className="flex items-center gap-1.5 truncate">
                        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                        <span className="truncate">{phaseName}</span>
                        <span className="text-[10px] text-slate-400 font-mono">({phaseTasks.length})</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] text-emerald-400">{phaseProgress}%</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenAddTask(phaseName);
                          }}
                          className="p-1 hover:bg-slate-700 text-slate-400 hover:text-white rounded"
                          title="Añadir actividad a esta fase"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </div>

                    {/* Task Rows under this Phase */}
                    {!isCollapsed && phaseTasks.map((task, idx) => (
                      <div
                        key={task.id}
                        className="h-12 hover:bg-white/[0.03] px-3 flex items-center justify-between text-xs transition-colors border-b border-[#1c1d26] group/item"
                      >
                        {/* WBS & Name */}
                        <div className="flex items-center gap-2 truncate flex-1 min-w-0 pr-2">
                          <button
                            onClick={() => handleToggleTaskComplete(task)}
                            className={`w-4 h-4 rounded border flex items-center justify-center cursor-pointer transition-colors ${
                              task.progressPercent === 100 
                                ? 'bg-emerald-600 border-emerald-500 text-white' 
                                : 'border-slate-600 hover:border-sky-400 text-transparent'
                            }`}
                            title={task.progressPercent === 100 ? 'Marcar como pendiente' : 'Marcar como 100% completada'}
                          >
                            <Check className="w-3 h-3 stroke-[3]" />
                          </button>

                          <span className="font-mono text-[10px] text-slate-400 shrink-0">
                            {task.wbsCode || `${idx + 1}`}
                          </span>

                          <span className={`truncate font-medium ${task.isMilestone ? 'text-sky-300 font-bold' : 'text-slate-200'}`} title={task.name}>
                            {task.isMilestone ? `🔷 ${task.name}` : task.name}
                          </span>

                          {task.status === 'BLOQUEADA' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsAlertCenterOpen(true);
                              }}
                              className="px-1.5 py-0.5 rounded text-[8px] font-black uppercase bg-red-950/90 text-red-300 border border-red-500/60 flex items-center gap-0.5 animate-pulse shrink-0 cursor-pointer"
                              title="⛔ Tarea Bloqueada - Clic para ver alerta PM"
                            >
                              <AlertOctagon className="w-2.5 h-2.5 text-red-400" />
                              <span>Bloqueada</span>
                            </button>
                          )}
                          {task.status === 'RETRASADA' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsAlertCenterOpen(true);
                              }}
                              className="px-1.5 py-0.5 rounded text-[8px] font-black uppercase bg-amber-950/90 text-amber-300 border border-amber-500/60 flex items-center gap-0.5 animate-pulse shrink-0 cursor-pointer"
                              title="⚠️ Tarea Retrasada - Clic para ver alerta PM"
                            >
                              <AlertTriangle className="w-2.5 h-2.5 text-amber-400" />
                              <span>Retrasada</span>
                            </button>
                          )}
                        </div>

                        {/* Assignee & Progress */}
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="w-6 h-6 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-[9px] font-bold text-slate-300" title={task.assignee}>
                            {task.assignee.split(' ').map(n => n[0]).slice(0, 2).join('')}
                          </div>

                          <span className="font-mono text-[11px] font-bold text-emerald-400 w-8 text-right">
                            {task.progressPercent}%
                          </span>

                          {/* Quick Actions */}
                          <div className="hidden group-hover/item:flex items-center gap-0.5">
                            <button
                              onClick={() => {
                                setEditingTask({ ...task, targetProjectId: task.projectId });
                                setIsTaskModalOpen(true);
                              }}
                              className="p-1 hover:bg-slate-700 text-slate-400 hover:text-white rounded"
                              title="Editar"
                            >
                              <Edit3 className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => handleDeleteTask(task)}
                              className="p-1 hover:bg-red-950/60 text-slate-500 hover:text-red-400 rounded"
                              title="Eliminar"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>

          </div>

          {/* RIGHT PANE: INTERACTIVE GANTT TIMELINE CANVAS */}
          <div className="flex-1 overflow-x-auto bg-[#16171d] relative">
            <div style={{ width: `${totalUnits * unitWidth}px` }} className="relative min-w-full">
              
              {/* Timeline Header (2 rows: Units & Sub-dates) */}
              <div className="h-14 border-b border-[#272935] bg-[#101115] sticky top-0 z-20 flex flex-col">
                <div className="flex border-b border-[#1c1d26] h-7">
                  {unitsList.map((u, i) => (
                    <div
                      key={i}
                      style={{ width: `${unitWidth}px` }}
                      className={`h-full flex items-center justify-center text-[10px] font-bold font-mono border-r border-[#232534] ${
                        u.isToday ? 'bg-red-950/40 text-red-400' : 'text-slate-300'
                      }`}
                    >
                      {u.label}
                    </div>
                  ))}
                </div>

                <div className="flex h-7">
                  {unitsList.map((u, i) => (
                    <div
                      key={i}
                      style={{ width: `${unitWidth}px` }}
                      className={`h-full flex items-center justify-center text-[9px] font-mono border-r border-[#232534] ${
                        u.isToday ? 'bg-red-950/30 text-red-300 font-bold' : u.isWeekend ? 'bg-slate-900/40 text-slate-500' : 'text-slate-400'
                      }`}
                    >
                      {u.subLabel}
                    </div>
                  ))}
                </div>
              </div>

              {/* Vertical Grid Columns */}
              <div className="absolute inset-0 top-14 pointer-events-none flex">
                {unitsList.map((u, i) => (
                  <div
                    key={i}
                    style={{ width: `${unitWidth}px` }}
                    className={`h-full border-r border-[#21232e]/60 ${
                      u.isWeekend ? 'bg-black/15' : ''
                    } ${u.isToday ? 'bg-red-500/[0.03]' : ''}`}
                  />
                ))}
              </div>

              {/* Current Date Vertical Marker ("HOY") */}
              {todayGeometry !== null && (
                <div
                  style={{ left: `${todayGeometry}px` }}
                  className="absolute top-0 bottom-0 z-15 pointer-events-none flex flex-col items-center"
                >
                  <div className="bg-red-600 text-white text-[9px] font-bold font-mono px-1.5 py-0.5 rounded-full shadow-lg -mt-1 shadow-red-600/50">
                    HOY
                  </div>
                  <div className="w-[1.5px] h-full bg-red-500/80 border-r border-red-400/40" />
                </div>
              )}

              {/* Gantt Rows corresponding to Phases and Tasks */}
              <div className="divide-y divide-[#21232e]">
                {Array.from(tasksByPhase.entries()).map(([phaseName, phaseTasks]) => {
                  const isCollapsed = collapsedPhases[phaseName];

                  return (
                    <div key={phaseName}>
                      {/* Phase Spacer Row */}
                      <div className="h-10 bg-[#1c1d26]/40 border-l-2 border-red-500/60" />

                      {/* Task Rows */}
                      {!isCollapsed && phaseTasks.map((task) => {
                        const geom = computeBarGeometry(task.startDate, task.endDate);

                        return (
                          <div key={task.id} className="h-12 relative flex items-center border-b border-[#1c1d26]">
                            
                            {geom.visible && (
                              task.isMilestone ? (
                                // Milestone Diamond Marker
                                <div
                                  style={{ left: `${geom.left}px` }}
                                  onClick={() => {
                                    setEditingTask({ ...task, targetProjectId: task.projectId });
                                    setIsTaskModalOpen(true);
                                  }}
                                  onMouseEnter={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setHoveredTask({ task, x: rect.left, y: rect.top });
                                  }}
                                  onMouseLeave={() => setHoveredTask(null)}
                                  className="absolute z-10 -ml-3 w-6 h-6 rotate-45 bg-sky-500 border-2 border-white shadow-lg shadow-sky-500/40 cursor-pointer hover:scale-125 transition-transform flex items-center justify-center"
                                >
                                  <div className="w-2 h-2 bg-white rounded-full" />
                                </div>
                              ) : (
                                // Standard Gantt Bar
                                <div
                                  style={{
                                    left: `${geom.left}px`,
                                    width: `${geom.width}px`
                                  }}
                                  onClick={() => {
                                    setEditingTask({ ...task, targetProjectId: task.projectId });
                                    setIsTaskModalOpen(true);
                                  }}
                                  onMouseEnter={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setHoveredTask({ task, x: rect.left, y: rect.top });
                                  }}
                                  onMouseLeave={() => setHoveredTask(null)}
                                  className={`absolute h-7 rounded-lg overflow-hidden cursor-pointer shadow-md transition-all hover:ring-2 hover:ring-white/80 group ${
                                    task.status === 'COMPLETADA' ? 'bg-emerald-950/80 border border-emerald-500/50' :
                                    task.status === 'RETRASADA' || task.status === 'BLOQUEADA' ? 'bg-rose-950/80 border border-rose-500/50' :
                                    task.status === 'EN_PROCESO' ? 'bg-sky-950/80 border border-sky-500/50' : 'bg-slate-800 border border-slate-700'
                                  }`}
                                >
                                  {/* Inner Progress Fill */}
                                  <div
                                    style={{ width: `${task.progressPercent}%` }}
                                    className={`h-full transition-all ${
                                      task.status === 'COMPLETADA' ? 'bg-emerald-500' :
                                      task.status === 'RETRASADA' || task.status === 'BLOQUEADA' ? 'bg-rose-500' :
                                      task.status === 'EN_PROCESO' ? 'bg-sky-500' : 'bg-slate-600'
                                    }`}
                                  />

                                  {/* Bar Text Label */}
                                  <div className="absolute inset-0 px-2 flex items-center justify-between text-[10px] font-bold text-white pointer-events-none drop-shadow">
                                    <div className="flex items-center gap-1 truncate pr-1">
                                      {task.status === 'BLOQUEADA' && <AlertOctagon className="w-3 h-3 text-red-300 animate-pulse shrink-0" />}
                                      {task.status === 'RETRASADA' && <AlertTriangle className="w-3 h-3 text-amber-300 animate-pulse shrink-0" />}
                                      <span className="truncate">{task.name}</span>
                                    </div>
                                    <span className="font-mono text-[9px] opacity-90">{task.progressPercent}%</span>
                                  </div>
                                </div>
                              )
                            )}

                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>

            </div>
          </div>

        </div>

      </div>

      {/* HOVER TASK TOOLTIP CARD */}
      {hoveredTask && (
        <div 
          style={{ top: `${hoveredTask.y - 120}px`, left: `${Math.min(hoveredTask.x, window.innerWidth - 320)}px` }}
          className="fixed z-50 pointer-events-none bg-[#0f1015] border border-[#2d3042] rounded-xl p-3 shadow-2xl text-xs text-white w-72 space-y-1.5 animate-in fade-in zoom-in-95 duration-100"
        >
          <div className="flex items-center justify-between">
            <span className="font-mono text-[10px] font-bold text-sky-400">WBS {hoveredTask.task.wbsCode}</span>
            {getTaskStatusPill(hoveredTask.task.status)}
          </div>
          <h5 className="font-bold text-white">{hoveredTask.task.name}</h5>
          <div className="text-[11px] text-slate-300 space-y-0.5">
            <div>👤 Resp: <strong className="text-white">{hoveredTask.task.assignee}</strong> ({hoveredTask.task.assigneeRole})</div>
            <div>📅 Periodo: <strong className="text-white">{hoveredTask.task.startDate} al {hoveredTask.task.endDate}</strong> ({hoveredTask.task.durationDays} días)</div>
            {hoveredTask.task.deliverable && <div>📦 Entregable: <strong className="text-amber-300">{hoveredTask.task.deliverable}</strong></div>}
            <div className="font-mono text-emerald-400 font-bold">Avance: {hoveredTask.task.progressPercent}%</div>
          </div>
        </div>
      )}

      {/* MODAL: AÑADIR / EDITAR ACTIVIDAD */}
      {isTaskModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[#16171e] border border-[#2e3144] rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4 text-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-[#2a2c3c] pb-3">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <Kanban className="w-5 h-5 text-red-500" />
                <span>{editingTask.name ? 'Editar Actividad del Plan' : 'Nueva Actividad / Tarea'}</span>
              </h3>
              <button
                onClick={() => setIsTaskModalOpen(false)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveTaskForm} className="space-y-4 text-xs">
              
              {/* Project and Phase Selection */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Proyecto Destino *</label>
                  <select
                    value={editingTask.targetProjectId || currentProject?.id || projects[0]?.id}
                    onChange={(e) => setEditingTask({ ...editingTask, targetProjectId: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                  >
                    {projects.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.code} - {p.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Fase del Proyecto *</label>
                  <input
                    type="text"
                    required
                    placeholder="Ej: Fase 1: Diagnóstico e Ingeniería"
                    value={editingTask.phase || ''}
                    onChange={(e) => setEditingTask({ ...editingTask, phase: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                  />
                </div>
              </div>

              {/* Task Name & WBS */}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Código WBS</label>
                  <input
                    type="text"
                    placeholder="1.1"
                    value={editingTask.wbsCode || ''}
                    onChange={(e) => setEditingTask({ ...editingTask, wbsCode: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white font-mono"
                  />
                </div>
                <div className="sm:col-span-3">
                  <label className="block text-slate-300 font-bold mb-1">Nombre de la Actividad / Tarea *</label>
                  <input
                    type="text"
                    required
                    placeholder="Ej: Pruebas hidrostáticas a 200 PSI"
                    value={editingTask.name || ''}
                    onChange={(e) => setEditingTask({ ...editingTask, name: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white font-bold"
                  />
                </div>
              </div>

              {/* Assignee and Role */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Responsable Asignado *</label>
                  <input
                    type="text"
                    required
                    placeholder="Ej: Ing. Carlos Mendoza"
                    value={editingTask.assignee || ''}
                    onChange={(e) => setEditingTask({ ...editingTask, assignee: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Rol Técnico</label>
                  <input
                    type="text"
                    placeholder="Ej: Especialista Termografía / Mecánico"
                    value={editingTask.assigneeRole || ''}
                    onChange={(e) => setEditingTask({ ...editingTask, assigneeRole: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                  />
                </div>
              </div>

              {/* Dates and Progress */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Fecha Inicio *</label>
                  <input
                    type="date"
                    required
                    value={editingTask.startDate || ''}
                    onChange={(e) => setEditingTask({ ...editingTask, startDate: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Fecha Fin *</label>
                  <input
                    type="date"
                    required
                    value={editingTask.endDate || ''}
                    onChange={(e) => setEditingTask({ ...editingTask, endDate: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 font-bold mb-1">% Avance (0-100)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={editingTask.progressPercent ?? 0}
                    onChange={(e) => setEditingTask({ ...editingTask, progressPercent: parseInt(e.target.value) || 0 })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white font-mono font-bold"
                  />
                </div>
              </div>

              {/* Status and Priority */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Estado de la Tarea</label>
                  <select
                    value={editingTask.status || 'POR_INICIAR'}
                    onChange={(e) => setEditingTask({ ...editingTask, status: e.target.value as any })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white font-bold"
                  >
                    <option value="POR_INICIAR">Por Iniciar</option>
                    <option value="EN_PROCESO">En Proceso</option>
                    <option value="COMPLETADA">Completada</option>
                    <option value="RETRASADA">⚠️ Retrasada (Genera Alerta PM)</option>
                    <option value="BLOQUEADA">🛑 Bloqueada (Genera Alerta PM)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Prioridad</label>
                  <select
                    value={editingTask.priority || 'ALTA'}
                    onChange={(e) => setEditingTask({ ...editingTask, priority: e.target.value as any })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                  >
                    <option value="CRITICA">Crítica</option>
                    <option value="ALTA">Alta</option>
                    <option value="MEDIA">Media</option>
                    <option value="BAJA">Baja</option>
                  </select>
                </div>
              </div>

              {/* AUTOMATED PM ALERT FIELDS IF TASK IS RETRASADA OR BLOQUEADA */}
              {(editingTask.status === 'RETRASADA' || editingTask.status === 'BLOQUEADA') && (
                <div className="p-3.5 rounded-xl bg-gradient-to-r from-rose-950/80 via-[#261017] to-[#16171e] border-2 border-rose-500/50 space-y-3 animate-in fade-in">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {editingTask.status === 'BLOQUEADA' ? (
                        <AlertOctagon className="w-4 h-4 text-red-400 shrink-0 animate-pulse" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 animate-pulse" />
                      )}
                      <span className="text-xs font-black text-rose-300 uppercase tracking-wider font-mono">
                        🚨 Notificación Automática al Responsable (PM)
                      </span>
                    </div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-rose-600/30 text-rose-200 border border-rose-500/40">
                      Disparo Automático al Guardar
                    </span>
                  </div>

                  <p className="text-[11px] text-slate-300">
                    Al guardar este cambio de estado, el sistema notificará inmediatamente al Project Manager ({projects.find(p => p.id === (editingTask.targetProjectId || currentProject?.id))?.manager || currentProject?.manager || 'Líder PM'}) y registrará la desviación en el panel de alertas.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                    <div>
                      <label className="block text-[11px] font-bold text-rose-200 mb-1">
                        Causa Raíz / Motivo del Desvío *
                      </label>
                      <input
                        type="text"
                        placeholder="Ej: Retraso en entrega de empaquetaduras por proveedor"
                        value={editingTask.alertReason || ''}
                        onChange={(e) => setEditingTask({ ...editingTask, alertReason: e.target.value })}
                        className="w-full bg-[#101115] border border-rose-500/40 rounded-xl p-2 text-white text-xs placeholder:text-slate-500 focus:outline-none focus:border-rose-400"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-rose-200 mb-1">
                        Plan de Mitigación / Acción Correctiva
                      </label>
                      <input
                        type="text"
                        placeholder="Ej: Doble turno técnico el fin de semana para recuperar holgura"
                        value={editingTask.alertMitigation || ''}
                        onChange={(e) => setEditingTask({ ...editingTask, alertMitigation: e.target.value })}
                        className="w-full bg-[#101115] border border-rose-500/40 rounded-xl p-2 text-white text-xs placeholder:text-slate-500 focus:outline-none focus:border-rose-400"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Deliverable and Milestone Toggle */}
              <div>
                <label className="block text-slate-300 font-bold mb-1">Entregable Clave / Evidencia</label>
                <input
                  type="text"
                  placeholder="Ej: Reporte NDT firmado, Dictamen UV, Acta recepción"
                  value={editingTask.deliverable || ''}
                  onChange={(e) => setEditingTask({ ...editingTask, deliverable: e.target.value })}
                  className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                />
              </div>

              <div className="flex items-center gap-2 p-3 bg-[#101115] border border-[#2a2c3c] rounded-xl">
                <input
                  type="checkbox"
                  id="isMilestoneCheckbox"
                  checked={!!editingTask.isMilestone}
                  onChange={(e) => setEditingTask({ ...editingTask, isMilestone: e.target.checked })}
                  className="w-4 h-4 rounded text-sky-600 focus:ring-sky-500 bg-slate-800 border-slate-700"
                />
                <label htmlFor="isMilestoneCheckbox" className="text-xs text-white font-bold cursor-pointer">
                  🔷 Es un Hito Crítico del Proyecto (Milestone)
                </label>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#2a2c3c]">
                <button
                  type="button"
                  onClick={() => setIsTaskModalOpen(false)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl font-bold cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-red-600 hover:bg-red-500 text-white rounded-xl font-bold cursor-pointer shadow"
                >
                  Guardar Actividad
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

      {/* MODAL: EDITAR INFORMACIÓN DEL PROYECTO */}
      {isEditProjectModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[#16171e] border border-[#2e3144] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4 text-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-[#2a2c3c] pb-3">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <Edit3 className="w-5 h-5 text-amber-400" />
                <span>Editar Proyecto: {editingProjectData.name || editingProjectData.code}</span>
              </h3>
              <button
                onClick={() => setIsEditProjectModalOpen(false)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveProjectForm} className="space-y-4 text-xs">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Código del Proyecto *</label>
                  <input
                    type="text"
                    required
                    placeholder="PRJ-BAJ-2025-01"
                    value={editingProjectData.code || ''}
                    onChange={(e) => setEditingProjectData({ ...editingProjectData, code: e.target.value.toUpperCase() })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white font-mono"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-slate-300 font-bold mb-1">Nombre del Proyecto *</label>
                  <input
                    type="text"
                    required
                    placeholder="Ej: Mantenimiento Mayor de Tina Pirolítica"
                    value={editingProjectData.name || ''}
                    onChange={(e) => setEditingProjectData({ ...editingProjectData, name: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white font-bold"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Cliente / Planta Operativa *</label>
                  <input
                    type="text"
                    required
                    placeholder="Ej: Mazda Salamanca"
                    value={editingProjectData.clientName || ''}
                    onChange={(e) => setEditingProjectData({ ...editingProjectData, clientName: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Líder PM / Responsable *</label>
                  <input
                    type="text"
                    required
                    placeholder="Ej: Ing. Alejandro Fuentes"
                    value={editingProjectData.manager || ''}
                    onChange={(e) => setEditingProjectData({ ...editingProjectData, manager: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Categoría</label>
                  <select
                    value={editingProjectData.category || 'MANTENIMIENTO'}
                    onChange={(e) => setEditingProjectData({ ...editingProjectData, category: e.target.value as any })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                  >
                    <option value="MANTENIMIENTO">Mantenimiento</option>
                    <option value="AUTOMATIZACION">Automatización</option>
                    <option value="OBRA_CIVIL">Obra Civil</option>
                    <option value="HSEQ">HSEQ / Seguridad</option>
                    <option value="SERVICIOS_ESPECIALIZADOS">Servicios Especializados</option>
                    <option value="LOGISTICA">Logística</option>
                  </select>
                </div>
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Estado</label>
                  <select
                    value={editingProjectData.status || 'POR_INICIAR'}
                    onChange={(e) => setEditingProjectData({ ...editingProjectData, status: e.target.value as any })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white font-bold"
                  >
                    <option value="POR_INICIAR">Por Iniciar</option>
                    <option value="EN_TIEMPO">En Tiempo</option>
                    <option value="EN_RIESGO">En Riesgo</option>
                    <option value="RETRASADO">Retrasado</option>
                    <option value="COMPLETADO">Completado</option>
                    <option value="PAUSADO">Pausado</option>
                  </select>
                </div>
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Prioridad</label>
                  <select
                    value={editingProjectData.priority || 'ALTA'}
                    onChange={(e) => setEditingProjectData({ ...editingProjectData, priority: e.target.value as any })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                  >
                    <option value="CRITICA">Crítica</option>
                    <option value="ALTA">Alta</option>
                    <option value="MEDIA">Media</option>
                    <option value="BAJA">Baja</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Fecha de Inicio *</label>
                  <input
                    type="date"
                    required
                    value={editingProjectData.startDate || ''}
                    onChange={(e) => setEditingProjectData({ ...editingProjectData, startDate: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 font-bold mb-1">Fecha Compromiso (Fin) *</label>
                  <input
                    type="date"
                    required
                    value={editingProjectData.targetEndDate || ''}
                    onChange={(e) => setEditingProjectData({ ...editingProjectData, targetEndDate: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 font-bold mb-1">% Avance (0 - 100)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={editingProjectData.progressPercent ?? 0}
                    onChange={(e) => setEditingProjectData({ ...editingProjectData, progressPercent: parseInt(e.target.value) || 0 })}
                    className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-300 font-bold mb-1">Descripción y Justificación</label>
                <textarea
                  rows={2}
                  placeholder="Detalles del alcance técnico, equipos involucrados y objetivos..."
                  value={editingProjectData.description || ''}
                  onChange={(e) => setEditingProjectData({ ...editingProjectData, description: e.target.value })}
                  className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-bold mb-1">Objetivos de Desempeño / Entregables</label>
                <input
                  type="text"
                  placeholder="Ej: Disponibilidad 99.5%, Certificado NOM-002, Acta firmada"
                  value={editingProjectData.scopeObjectives || ''}
                  onChange={(e) => setEditingProjectData({ ...editingProjectData, scopeObjectives: e.target.value })}
                  className="w-full bg-[#101115] border border-[#2a2c3c] rounded-xl p-2.5 text-white"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#2a2c3c]">
                <button
                  type="button"
                  onClick={() => setIsEditProjectModalOpen(false)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl font-bold cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-red-600 hover:bg-red-500 text-white rounded-xl font-bold cursor-pointer shadow-md"
                >
                  Guardar Cambios
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: CENTRO DE ALERTAS Y NOTIFICACIONES AL RESPONSABLE (PM) */}
      {isAlertCenterOpen && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-3 sm:p-5 backdrop-blur-sm animate-in fade-in">
          <div className="bg-[#14151c] border-2 border-[#2f3246] rounded-3xl max-w-4xl w-full max-h-[92vh] flex flex-col text-white shadow-2xl overflow-hidden">
            
            {/* Modal Header */}
            <div className="p-5 border-b border-[#262838] bg-[#101117] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-rose-600/20 border border-rose-500/50 flex items-center justify-center text-rose-400 shrink-0">
                  <BellRing className="w-5 h-5 animate-pulse" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-bold text-white">
                      Centro de Alertas PM - Plan de Trabajo
                    </h3>
                    <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-rose-950 text-rose-300 border border-rose-500/50 font-mono">
                      {businessUnitName}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400">
                    Notificaciones automáticas y seguimiento a tareas en estado RETRASADA o BLOQUEADA.
                  </p>
                </div>
              </div>

              <button
                onClick={() => setIsAlertCenterOpen(false)}
                className="p-2 hover:bg-slate-800 rounded-xl text-slate-400 hover:text-white transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* KPI Metrics & Mass Action Bar */}
            <div className="p-4 bg-[#181922] border-b border-[#262838] grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-[#101115] border border-[#272935] rounded-2xl p-3 flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Tareas Críticas</div>
                  <div className="text-xl font-black text-white font-mono">{criticalAlertTasks.length}</div>
                </div>
                <div className="w-8 h-8 rounded-xl bg-slate-800 flex items-center justify-center text-slate-300">
                  <ShieldAlert className="w-4 h-4 text-rose-400" />
                </div>
              </div>

              <div className="bg-[#101115] border border-red-500/30 rounded-2xl p-3 flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-bold text-red-400 uppercase tracking-wider">Tareas Bloqueadas</div>
                  <div className="text-xl font-black text-red-300 font-mono">{blockedTasksCount}</div>
                </div>
                <div className="w-8 h-8 rounded-xl bg-red-950/80 border border-red-500/40 flex items-center justify-center text-red-400">
                  <AlertOctagon className="w-4 h-4" />
                </div>
              </div>

              <div className="bg-[#101115] border border-amber-500/30 rounded-2xl p-3 flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">Tareas Retrasadas</div>
                  <div className="text-xl font-black text-amber-300 font-mono">{delayedTasksCount}</div>
                </div>
                <div className="w-8 h-8 rounded-xl bg-amber-950/80 border border-amber-500/40 flex items-center justify-center text-amber-400">
                  <AlertTriangle className="w-4 h-4" />
                </div>
              </div>
            </div>

            {/* Modal Body / Alerts List */}
            <div className="flex-1 overflow-y-auto p-5 space-y-3.5">
              {criticalAlertTasks.length === 0 ? (
                <div className="text-center py-12 px-4 bg-[#101115] border border-[#272935] rounded-3xl space-y-2">
                  <div className="w-12 h-12 rounded-2xl bg-emerald-950/60 border border-emerald-500/40 mx-auto flex items-center justify-center text-emerald-400">
                    <Check className="w-6 h-6 stroke-[3]" />
                  </div>
                  <h4 className="text-sm font-bold text-white">¡No hay tareas con alertas críticas!</h4>
                  <p className="text-xs text-slate-400 max-w-md mx-auto">
                    Todas las actividades del cronograma se encuentran En Tiempo, Por Iniciar o Completadas. No se requieren acciones correctivas de emergencia.
                  </p>
                </div>
              ) : (
                criticalAlertTasks.map((task) => {
                  const parentPrj = projects.find(p => p.id === task.projectId);
                  const isBlocked = task.status === 'BLOQUEADA';
                  const pmName = task.projectManager || parentPrj?.manager || 'Líder PM';
                  const projectCode = task.projectCode || parentPrj?.code || 'PRJ';
                  const projectName = task.projectName || parentPrj?.name || 'Proyecto';
                  const clientName = task.clientName || parentPrj?.clientName || 'Cliente';

                  const alertObj = {
                    businessUnit: parentPrj?.businessUnit || businessUnitName,
                    projectCode,
                    projectName,
                    pmName,
                    status: task.status,
                    wbsCode: task.wbsCode || '1.0',
                    taskName: task.name,
                    assignee: task.assignee,
                    endDate: task.endDate,
                    reason: task.notes || (isBlocked ? 'Bloqueo operativo reportado en Gantt' : 'Desviación en cronograma de ejecución'),
                    mitigationPlan: 'Seguimiento prioritario y escalamiento con el PM responsable'
                  };

                  return (
                    <div 
                      key={task.id}
                      className={`p-4 rounded-2xl border transition-all ${
                        isBlocked 
                          ? 'bg-[#181114] border-red-500/50 shadow-lg shadow-red-950/30' 
                          : 'bg-[#191512] border-amber-500/40 shadow-lg shadow-amber-950/20'
                      }`}
                    >
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 pb-2.5 border-b border-white/[0.06]">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase flex items-center gap-1 font-mono ${
                            isBlocked ? 'bg-red-600 text-white' : 'bg-amber-600 text-black'
                          }`}>
                            {isBlocked ? <AlertOctagon className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                            {task.status}
                          </span>
                          <span className="font-mono text-xs font-bold text-sky-400 bg-sky-950/60 px-2 py-0.5 rounded border border-sky-500/30">
                            {projectCode}
                          </span>
                          <span className="text-xs font-bold text-white">{projectName}</span>
                          <span className="text-[11px] text-slate-400">({clientName})</span>
                        </div>

                        <div className="flex items-center gap-1.5 text-xs text-slate-300">
                          <span className="text-slate-400">Líder PM:</span>
                          <strong className="text-rose-300 font-bold bg-rose-950/80 px-2 py-0.5 rounded border border-rose-500/40">
                            👤 {pmName}
                          </strong>
                        </div>
                      </div>

                      <div className="py-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                        <div className="space-y-1">
                          <div className="text-slate-300">
                            <span className="font-mono text-[10px] text-slate-400 font-bold mr-1">WBS {task.wbsCode}:</span>
                            <strong className="text-white text-sm">{task.name}</strong>
                          </div>
                          <div className="text-slate-400 text-[11px]">
                            Fase: <span className="text-slate-200">{task.phase}</span> • Asignado a: <strong className="text-white">{task.assignee}</strong> ({task.assigneeRole})
                          </div>
                          <div className="text-slate-400 text-[11px]">
                            Ventana: <span className="text-slate-200">{task.startDate} al {task.endDate}</span> ({task.durationDays} días) • Avance actual: <strong className="text-emerald-400 font-mono">{task.progressPercent}%</strong>
                          </div>
                        </div>

                        <div className="bg-black/30 border border-white/[0.08] rounded-xl p-2.5 space-y-1.5 text-[11px]">
                          <div className="flex items-start gap-1.5 text-rose-200">
                            <span className="font-bold shrink-0">Motivo / Causa:</span>
                            <span className="text-slate-200">{alertObj.reason}</span>
                          </div>
                          <div className="flex items-start gap-1.5 text-amber-200">
                            <span className="font-bold shrink-0">Mitigación:</span>
                            <span className="text-slate-200">{alertObj.mitigationPlan}</span>
                          </div>
                        </div>
                      </div>

                      {/* Action Dispatch Buttons */}
                      <div className="pt-2.5 border-t border-white/[0.06] flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => handleSendWhatsAppAlert(alertObj)}
                            className="px-3 py-1.5 rounded-xl bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-300 border border-emerald-500/40 text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer"
                            title="Enviar Notificación WhatsApp al PM"
                          >
                            <Send className="w-3.5 h-3.5" />
                            <span>WhatsApp PM</span>
                          </button>

                          <button
                            onClick={() => handleSendEmailAlert(alertObj)}
                            className="px-3 py-1.5 rounded-xl bg-sky-600/20 hover:bg-sky-600/30 text-sky-300 border border-sky-500/40 text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer"
                            title="Enviar Notificación por Correo al PM"
                          >
                            <Mail className="w-3.5 h-3.5" />
                            <span>Correo PM</span>
                          </button>

                          <button
                            onClick={() => handleCopyAlertReport(alertObj, task.id)}
                            className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer"
                            title="Copiar texto de la alerta"
                          >
                            <Copy className="w-3.5 h-3.5" />
                            <span>{copiedAlertId === task.id ? '¡Copiado!' : 'Copiar'}</span>
                          </button>
                        </div>

                        <button
                          onClick={() => {
                            setEditingTask({ ...task, targetProjectId: task.projectId });
                            setIsTaskModalOpen(true);
                          }}
                          className="px-3.5 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold flex items-center gap-1.5 cursor-pointer border border-slate-600"
                        >
                          <Edit3 className="w-3.5 h-3.5 text-amber-400" />
                          <span>Actualizar / Resolver Tarea</span>
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-[#262838] bg-[#101117] flex items-center justify-between text-xs text-slate-400">
              <div className="flex items-center gap-2 font-mono text-[11px]">
                <Clock className="w-3.5 h-3.5 text-slate-500" />
                <span>Monitoreo en tiempo real del Gantt</span>
              </div>
              <button
                onClick={() => setIsAlertCenterOpen(false)}
                className="px-5 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl font-bold cursor-pointer transition-colors"
              >
                Cerrar Panel
              </button>
            </div>

          </div>
        </div>
      )}

      {/* GANTT EXPORT MODAL (PDF & EXCEL WITH EXECUTIVE SUMMARY & PROGRESS BARS) */}
      {isGanttExportModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-150">
          <div className="bg-[#151722] border border-[#2d3148] w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">
            
            {/* Modal Header */}
            <div className="p-5 border-b border-[#262838] bg-[#11131c] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center text-white shadow-lg shadow-emerald-950/50">
                  <Download className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <span>Exportar Cronograma Gantt y Plan de Trabajo</span>
                    <span className="text-[10px] uppercase font-mono px-2 py-0.5 rounded-full bg-emerald-950 text-emerald-300 border border-emerald-500/40">
                      Oficial PMO
                    </span>
                  </h3>
                  <p className="text-xs text-slate-400">
                    Generación de expedientes ejecutivos en PDF y hojas de cálculo avanzadas en Excel con seguimiento de barras de progreso.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsGanttExportModalOpen(false)}
                className="text-slate-400 hover:text-white p-2 rounded-xl hover:bg-slate-800 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto space-y-6 flex-1 text-slate-300 text-xs">
              
              {/* Project Scope Selection */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-200 uppercase tracking-wider block">
                  Seleccionar Alcance del Informe:
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setExportTargetId(activeProjectId !== 'ALL' ? activeProjectId : (projects[0]?.id || 'ALL'))}
                    className={`p-3 rounded-2xl border text-left transition-all cursor-pointer flex items-center gap-3 ${
                      exportTargetId !== 'ALL'
                        ? 'bg-emerald-950/40 border-emerald-500 text-white shadow-md'
                        : 'bg-[#1a1d29] border-[#2d3148] text-slate-400 hover:border-slate-600'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${
                      exportTargetId !== 'ALL' ? 'bg-emerald-500 text-slate-950' : 'bg-slate-800 text-slate-400'
                    }`}>
                      <Target className="w-4 h-4 font-bold" />
                    </div>
                    <div>
                      <div className="font-bold text-xs">Proyecto Específico</div>
                      <div className="text-[11px] text-slate-400">Dossier detallado con Gantt y WBS</div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setExportTargetId('ALL')}
                    className={`p-3 rounded-2xl border text-left transition-all cursor-pointer flex items-center gap-3 ${
                      exportTargetId === 'ALL'
                        ? 'bg-emerald-950/40 border-emerald-500 text-white shadow-md'
                        : 'bg-[#1a1d29] border-[#2d3148] text-slate-400 hover:border-slate-600'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${
                      exportTargetId === 'ALL' ? 'bg-emerald-500 text-slate-950' : 'bg-slate-800 text-slate-400'
                    }`}>
                      <FolderDown className="w-4 h-4 font-bold" />
                    </div>
                    <div>
                      <div className="font-bold text-xs">Portafolio Completo</div>
                      <div className="text-[11px] text-slate-400">Todos los proyectos de {businessUnitName}</div>
                    </div>
                  </button>
                </div>

                {/* Dropdown if specific project is chosen */}
                {exportTargetId !== 'ALL' && (
                  <div className="pt-2">
                    <select
                      value={exportTargetId}
                      onChange={(e) => setExportTargetId(e.target.value)}
                      className="w-full bg-[#11131c] border border-[#2d3148] rounded-xl px-3 py-2 text-xs text-white font-medium focus:outline-none focus:border-emerald-500"
                    >
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.code} - {p.name} ({p.progressPercent}% avance)
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Preview Info Box */}
              {(() => {
                const targetProj = exportTargetId === 'ALL' ? null : projects.find(p => p.id === exportTargetId);
                if (targetProj) {
                  const totalTasks = targetProj.tasks.length;
                  const completedTasks = targetProj.tasks.filter(t => t.status === 'COMPLETADA').length;
                  const inProgressTasks = targetProj.tasks.filter(t => t.status === 'EN_PROGRESO').length;
                  const blockedTasks = targetProj.tasks.filter(t => t.status === 'BLOQUEADA' || t.status === 'RETRASADA').length;

                  return (
                    <div className="bg-[#11131c] border border-[#272a3d] rounded-2xl p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-emerald-950 text-emerald-400 font-mono text-[11px] font-bold rounded border border-emerald-500/30">
                            {targetProj.code}
                          </span>
                          <span className="font-bold text-white text-sm">{targetProj.name}</span>
                        </div>
                        <span className="text-[11px] text-slate-400">PM: <strong className="text-white">{targetProj.manager}</strong></span>
                      </div>

                      {/* Progress bar */}
                      <div className="space-y-1">
                        <div className="flex justify-between text-[11px]">
                          <span className="text-slate-400">Avance Ponderado del Cronograma:</span>
                          <span className="font-mono font-bold text-emerald-400">{targetProj.progressPercent}%</span>
                        </div>
                        <div className="w-full bg-[#1a1d29] h-2.5 rounded-full overflow-hidden border border-[#2d3148]">
                          <div
                            className="bg-gradient-to-r from-emerald-500 to-teal-400 h-full rounded-full transition-all duration-300"
                            style={{ width: `${targetProj.progressPercent}%` }}
                          />
                        </div>
                      </div>

                      {/* Summary Badges */}
                      <div className="grid grid-cols-4 gap-2 pt-1 text-center font-mono">
                        <div className="bg-[#181a24] p-2 rounded-xl border border-[#282a3c]">
                          <div className="text-[10px] text-slate-400">Actividades</div>
                          <div className="text-sm font-bold text-white">{totalTasks}</div>
                        </div>
                        <div className="bg-[#181a24] p-2 rounded-xl border border-[#282a3c]">
                          <div className="text-[10px] text-emerald-400">Completadas</div>
                          <div className="text-sm font-bold text-emerald-400">{completedTasks}</div>
                        </div>
                        <div className="bg-[#181a24] p-2 rounded-xl border border-[#282a3c]">
                          <div className="text-[10px] text-sky-400">En Progreso</div>
                          <div className="text-sm font-bold text-sky-400">{inProgressTasks}</div>
                        </div>
                        <div className="bg-[#181a24] p-2 rounded-xl border border-[#282a3c]">
                          <div className="text-[10px] text-rose-400">En Alerta</div>
                          <div className="text-sm font-bold text-rose-400">{blockedTasks}</div>
                        </div>
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div className="bg-[#11131c] border border-[#272a3d] rounded-2xl p-4 space-y-2 text-center">
                      <div className="text-white font-bold">Portafolio General: {businessUnitName}</div>
                      <div className="text-slate-400 text-xs">
                        Se compilarán los {projects.length} proyectos registrados con sus cronogramas de avance, presupuestos y matrices de actividades.
                      </div>
                    </div>
                  );
                }
              })()}

              {/* Export Format Action Cards */}
              <div className="space-y-3">
                <label className="text-xs font-bold text-slate-200 uppercase tracking-wider block">
                  Formatos de Exportación Disponibles:
                </label>

                {/* Option 1: PDF Dossier with Visual Gantt */}
                <div className="p-4 rounded-2xl bg-[#191b26] border border-[#2c2f44] flex items-center justify-between gap-4 hover:border-rose-500/50 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-rose-950/60 border border-rose-500/50 flex items-center justify-center text-rose-300 shrink-0">
                      <FileText className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-bold text-white text-xs flex items-center gap-2">
                        <span>Expediente PDF con Diagrama de Gantt</span>
                        <span className="text-[10px] bg-rose-950 text-rose-300 px-2 py-0.5 rounded border border-rose-500/30">
                          Resumen Ejecutivo
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        Incluye cronograma visual con barras de progreso a color, KPIs estratégicos, horizonte temporal, lista de actividades y firmas formales.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      handleExportGanttPDF(exportTargetId);
                      setIsGanttExportModalOpen(false);
                    }}
                    className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-500 hover:to-red-500 text-white font-bold text-xs flex items-center gap-1.5 shrink-0 shadow-lg shadow-rose-950/50 cursor-pointer"
                  >
                    <Download className="w-4 h-4" />
                    <span>Descargar PDF</span>
                  </button>
                </div>

                {/* Option 2: Advanced Excel with Visual Timeline */}
                <div className="p-4 rounded-2xl bg-[#191b26] border border-[#2c2f44] flex items-center justify-between gap-4 hover:border-emerald-500/50 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-950/60 border border-emerald-500/50 flex items-center justify-center text-emerald-300 shrink-0">
                      <FileSpreadsheet className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-bold text-white text-xs flex items-center gap-2">
                        <span>Libro de Control Gantt en Excel (.xlsx)</span>
                        <span className="text-[10px] bg-emerald-950 text-emerald-300 px-2 py-0.5 rounded border border-emerald-500/30">
                          Matriz Dinámica
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        Matriz Gantt semanal con seguimiento visual condicional, fórmulas de avance, entregables WBS y matriz de responsables RACI.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      await handleExportGanttExcel(exportTargetId);
                      setIsGanttExportModalOpen(false);
                    }}
                    className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs flex items-center gap-1.5 shrink-0 shadow-lg shadow-emerald-950/50 cursor-pointer"
                  >
                    <Download className="w-4 h-4" />
                    <span>Descargar Excel</span>
                  </button>
                </div>

                {/* Option 3: Download Both */}
                <div className="p-4 rounded-2xl bg-gradient-to-r from-blue-950/40 via-indigo-950/40 to-purple-950/40 border border-indigo-500/40 flex items-center justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-indigo-600/30 border border-indigo-500/60 flex items-center justify-center text-indigo-300 shrink-0">
                      <Layers className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-bold text-white text-xs flex items-center gap-2">
                        <span>Paquete Completo (PDF + Excel)</span>
                        <span className="text-[10px] bg-indigo-950 text-indigo-300 px-2 py-0.5 rounded border border-indigo-500/30 font-bold">
                          Recomendado PMO
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        Descarga en simultáneo el informe imprimible en PDF y el archivo editable de cálculo en Excel.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      await handleExportGanttBoth(exportTargetId);
                      setIsGanttExportModalOpen(false);
                    }}
                    className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500 text-white font-bold text-xs flex items-center gap-1.5 shrink-0 shadow-lg shadow-indigo-950/50 cursor-pointer"
                  >
                    <Download className="w-4 h-4" />
                    <span>Descargar Ambos</span>
                  </button>
                </div>

              </div>

            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-[#262838] bg-[#101117] flex items-center justify-between text-xs text-slate-400">
              <div className="flex items-center gap-2 text-[11px]">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span>Formatos estandarizados conforme a directrices de Grupo ISN</span>
              </div>
              <button
                onClick={() => setIsGanttExportModalOpen(false)}
                className="px-5 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl font-bold cursor-pointer transition-colors"
              >
                Cerrar
              </button>
            </div>

          </div>
        </div>
      )}

      {/* FLOATING TOAST PM NOTIFICATION */}
      {activeToast && (
        <div className="fixed bottom-6 right-6 z-50 max-w-md w-full bg-[#181116] border-2 border-rose-500/80 rounded-2xl p-4 text-white shadow-2xl shadow-rose-950/80 flex items-start justify-between gap-3 animate-in slide-in-from-bottom-5 fade-in duration-200">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-rose-600/30 border border-rose-500/60 flex items-center justify-center text-rose-300 shrink-0 mt-0.5 animate-pulse">
              <BellRing className="w-5 h-5" />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-black text-rose-400 uppercase tracking-wider font-mono">
                  {activeToast.title}
                </span>
              </div>
              <p className="text-xs text-slate-200 font-medium">
                {activeToast.message}
              </p>
              <div className="pt-1.5 flex items-center gap-2">
                <button
                  onClick={() => {
                    setIsAlertCenterOpen(true);
                    setActiveToast(null);
                  }}
                  className="px-2.5 py-1 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-[11px] font-bold cursor-pointer transition-colors"
                >
                  Abrir Centro de Alertas
                </button>
                <button
                  onClick={() => setActiveToast(null)}
                  className="px-2 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-[11px] font-medium cursor-pointer"
                >
                  Descartar
                </button>
              </div>
            </div>
          </div>

          <button
            onClick={() => setActiveToast(null)}
            className="text-slate-400 hover:text-white p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

    </div>
  );
};
